import React from 'react';
import { CheckCircle2, Target, Globe2, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-[#FAFAF8] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-[#111111] mb-4">About Rock Granites</h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            India's trusted partner for premium granite export. Building long-term relationships through quality, consistency, and reliability.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111111] mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="!font-normal">Rock Granites was founded with a singular vision: to be the most reliable Indian granite exporter for international markets. For over five decades, we have built our reputation on consistent quality, transparent communication, and unwavering commitment to our clients' success.

                </p>
                <p>By sourcing granite directly from quarries and processing it in our in-house manufacturing units, we retain end-to-end oversight of quality, grading, and finishes, allowing us to deliver reliable output at scale for B2B projects.

                </p>
                <p>Our expertise spans the entire export lifecycle, from material selection and processing to packaging, documentation, and international logistics. We understand that successful projects depend on reliable supply chains, which is why our clients return to us for their most critical requirements.

                </p>
              </div>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_ad7e8f87-1d9a-44c7-94bb-e1bf1711e4b9/artifacts/95wr0quq_hf_20260124_141836_27ad9bb5-a16d-449b-b323-61a5ea075ac0.png"
                alt="Rock Granites Export Operations"
                className="w-full h-full object-cover" />

            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-12 text-center">Our Commitments</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-0 shadow-sm text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Quality First</h3>
                <p className="text-sm text-gray-600">
                  Rigorous multi-stage quality control ensures consistent grading across all shipments
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Reliability</h3>
                <p className="text-sm text-gray-600">
                  On-time delivery and transparent communication throughout the supply chain
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe2 className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Global Reach</h3>
                <p className="text-sm text-gray-600">
                  Serving 40+ countries with comprehensive export documentation and logistics
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-[#111111]" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Partnerships</h3>
                <p className="text-sm text-gray-600">
                  Long-term relationships built on trust, transparency, and mutual success
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#111111] mb-12 text-center">Quarry to Export Workflow</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="relative">
              <div className="bg-[#FAFAF8] rounded-lg p-6 h-full">
                <div className="w-12 h-12 bg-[#111111] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Quarry Selection</h3>
                <p className="text-sm text-gray-600">
                  Direct sourcing from verified quarries. Block selection based on color consistency and quality parameters.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#FAFAF8] rounded-lg p-6 h-full">
                <div className="w-12 h-12 bg-[#111111] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Processing</h3>
                <p className="text-sm text-gray-600">
                  State-of-the-art cutting and finishing facilities. Multiple quality checkpoints during production.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#FAFAF8] rounded-lg p-6 h-full">
                <div className="w-12 h-12 bg-[#111111] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Quality Control</h3>
                <p className="text-sm text-gray-600">
                  Comprehensive grading and inspection. Detailed documentation for traceability.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-[#FAFAF8] rounded-lg p-6 h-full">
                <div className="w-12 h-12 bg-[#111111] text-white rounded-full flex items-center justify-center mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="text-lg font-semibold mb-2 text-[#111111]">Export & Delivery</h3>
                <p className="text-sm text-gray-600">
                  Secure packaging, complete documentation, and reliable global shipping to your location.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-[#111111] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Why International Buyers Choose Us</h2>
          <p className="!text-lg mb-12 text-gray-300">We understand that consistency, reliability, and transparent communication are critical for international procurement. Our clients choose Rock Granites because we deliver on our promises, every single time.

          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <h3 className="!font-bold !text-2xl mb-2">50+</h3>
              <p className="text-gray-400">Years of Export Experience</p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">40+</h3>
              <p className="text-gray-400">Countries Served</p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">10,000+</h3>
              <p className="text-gray-400">Containers Shipped</p>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default About;